import { resolveHttpResourceUrl } from "../../../lib/http/resourceUrl";
import { Button, Image, Tooltip } from "@arco-design/web-react";
import { ImageUp, Trash2 } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { uploadMedia } from "../../media/repo";

type ResourcePointImageFieldProps = {
  value?: string;
  onChange: (url: string) => void;
  onUploadingChange?: (uploading: boolean) => void;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
  "aria-labelledby"?: string;
  "aria-required"?: boolean;
};

/** 上传、预览和移除考察点 SPU 的单张头图，不删除远端媒体对象。 */
export function ResourcePointImageField({
  value,
  onChange,
  onUploadingChange,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  "aria-labelledby": ariaLabelledBy,
  "aria-required": ariaRequired,
}: ResourcePointImageFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const actionTextId = useId();
  const isMountedRef = useRef(true);
  const uploadingRef = useRef(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /** 选择文件后上传，并在失败时保留当前已保存的图片。 */
  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || uploadingRef.current) return;
    if (!file.type.startsWith("image/")) {
      setError("请选择图片文件。");
      return;
    }

    uploadingRef.current = true;
    setError("");
    setUploading(true);
    onUploadingChange?.(true);
    try {
      const result = await uploadMedia(file, "image");
      if (isMountedRef.current) onChange(result.fileUrl);
    } catch {
      if (isMountedRef.current) setError("图片上传失败，请重试。");
    } finally {
      uploadingRef.current = false;
      if (isMountedRef.current) {
        setUploading(false);
        onUploadingChange?.(false);
      }
    }
  }

  /** 打开隐藏的图片选择控件。 */
  function openFilePicker() {
    if (!uploading) inputRef.current?.click();
  }

  const actionText = uploading ? "上传中..." : value ? "重新上传" : "上传图片";
  const buttonLabelledBy = [ariaLabelledBy, actionTextId].filter(Boolean).join(" ");

  return (
    <div className="resource-point-image-field">
      {value ? (
        <Image
          className="resource-point-image-field__preview"
          src={resolveHttpResourceUrl(value)}
          alt="头图预览"
          preview
        />
      ) : null}
      <div className="resource-point-image-field__actions">
        <Button
          type="secondary"
          icon={<ImageUp size={16} aria-hidden="true" />}
          loading={uploading}
          disabled={uploading}
          aria-labelledby={buttonLabelledBy}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          aria-required={ariaRequired}
          onClick={openFilePicker}
        >
          <span id={actionTextId}>{actionText}</span>
        </Button>
        {value ? (
          <Tooltip content="移除图片">
            <Button
              type="text"
              status="danger"
              icon={<Trash2 size={16} aria-hidden="true" />}
              aria-label="移除图片"
              disabled={uploading}
              onClick={() => onChange("")}
            />
          </Tooltip>
        ) : null}
      </div>
      <input
        ref={inputRef}
        className="resource-point-image-field__input"
        data-testid="resource-point-image-input"
        type="file"
        accept="image/*"
        tabIndex={-1}
        aria-hidden="true"
        disabled={uploading}
        onChange={handleFileChange}
      />
      {error ? <div className="resource-point-image-field__error" role="alert">{error}</div> : null}
    </div>
  );
}
